import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { LAYOUT_POSITIONS } from './equipmentDefs';
import { GROUND_BASE, GROUND_GLOW } from './twinColors';

const GRID_SIZE = 100;
const POINT_SPACING = 2.0;
const BASE_COLOR = new THREE.Color(GROUND_BASE);
const GLOW_COLOR = new THREE.Color(GROUND_GLOW);

/**
 * HexGround - Particle field ground plane replacing the shader-based hex grid.
 *
 * Scattered points on the XZ plane that glow brighter near active equipment.
 * Matches the holographic point cloud aesthetic.
 *
 * @param {{ activeEquipment: string[] }} props
 */
export default function HexGround({ activeEquipment = [] }) {
    const pointsRef = useRef();

    // Stabilize dependency — avoid regenerating thousands of particles on every render
    // when parent passes Object.keys(eqStates) which creates a new array each time
    const activeKey = useMemo(() => [...activeEquipment].sort().join(','), [activeEquipment]);

    const { positions, baseColors, count } = useMemo(() => {
        const pts = [];
        const cols = [];
        const half = GRID_SIZE / 2;

        // Equipment pad positions for glow calculation
        const pads = activeKey.split(',').filter(Boolean)
            .map(k => LAYOUT_POSITIONS[k])
            .filter(Boolean)
            .map(p => [p[0], p[2]]);

        for (let x = -half; x <= half; x += POINT_SPACING) {
            for (let z = -half; z <= half; z += POINT_SPACING) {
                const dist = Math.sqrt(x * x + z * z);
                if (dist > half) continue;

                // Edge fade probability
                const prob = 1 - (dist / half) * 0.85;
                if (Math.random() > prob) continue;

                // Small random offset for organic feel
                pts.push(
                    x + (Math.random() - 0.5) * 0.8,
                    (Math.random() - 0.5) * 0.03,
                    z + (Math.random() - 0.5) * 0.8
                );

                // Color: base deep blue, brighter near equipment
                let padGlow = 0;
                for (const [px, pz] of pads) {
                    const d = Math.sqrt((x - px) ** 2 + (z - pz) ** 2);
                    padGlow = Math.max(padGlow, 1 - Math.min(d / 15, 1));
                }

                const color = BASE_COLOR.clone().lerp(GLOW_COLOR, padGlow * 0.6);
                cols.push(color.r, color.g, color.b);
            }
        }

        return {
            positions: new Float32Array(pts),
            baseColors: new Float32Array(cols),
            count: pts.length / 3,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKey]);

    // Subtle breathing animation
    useFrame(({ clock }) => {
        if (!pointsRef.current) return;
        const mat = pointsRef.current.material;
        mat.opacity = 0.25 + Math.sin(clock.elapsedTime * 0.3) * 0.05;
    });

    return (
        <points ref={pointsRef} position={[0, -0.01, 0]}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={count}
                    array={positions}
                    itemSize={3}
                />
                <bufferAttribute
                    attach="attributes-color"
                    count={count}
                    array={baseColors}
                    itemSize={3}
                />
            </bufferGeometry>
            <pointsMaterial
                size={0.12}
                transparent
                opacity={0.25}
                depthWrite={false}
                sizeAttenuation
                vertexColors
                blending={THREE.AdditiveBlending}
            />
        </points>
    );
}
